import axios from "axios";
import { CheckToken } from "../../login/AccessToken";
import { listLookups } from "../../ipd-billing/service-master-api";

/**
 * Diagnostic Doctor admin APIs — Radiologist / Pathologist profiles with login
 * users. Separate from add_new_user/ + get_all_doctors/.
 *
 *   add_diagnostic_doctor/         POST create (auth_user + profile)
 *   get_all_diagnostic_doctors/    GET  list (?role_type=)
 *   edit_diagnostic_doctor/        POST update (password optional)
 *   delete_diagnostic_doctor/      POST delete
 */

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

export const DIAGNOSTIC_DOCTOR_ROLES = [
  { value: "radiologist", label: "Radiologist" },
  { value: "pathologist", label: "Pathologist" },
];

/** Sentinel for the "All classifications" dropdown option → API sends null. */
export const CLASSIFICATION_ALL = "__all__";

const SALUTATIONS = [
  "Mr",
  "Mrs",
  "Ms",
  "Miss",
  "Dr",
  "Prof",
  "Master",
  "Rev",
  "Mx",
];

export { SALUTATIONS };

function authHeaders() {
  CheckToken();
  return {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  };
}

function norm(s) {
  return String(s || "").toLowerCase();
}

/** Match service group from lookups when role changes (Radiology / Laboratory). */
export function resolveServiceGroupForRole(roleType, groups = []) {
  const role = norm(roleType);
  if (role === "radiologist") {
    return (
      groups.find(
        (g) =>
          norm(g.code).includes("radiolog") || norm(g.name).includes("radiolog")
      ) || null
    );
  }
  if (role === "pathologist") {
    return (
      groups.find(
        (g) =>
          norm(g.code).includes("patholog") ||
          norm(g.name).includes("patholog") ||
          norm(g.code).includes("laborator") ||
          norm(g.name).includes("laborator") ||
          norm(g.name).includes("laboratory")
      ) || null
    );
  }
  return null;
}

export function filterClassificationsForGroup(classifications = [], groupId) {
  if (!groupId) return [];
  const gid = Number(groupId);
  return classifications.filter((c) => Number(c.groupId) === gid);
}

let cachedLookups = null;
let lookupsInflight = null;

export async function fetchServiceMasterLookups({ force = false } = {}) {
  if (!force && cachedLookups) {
    return cachedLookups;
  }
  if (!force && lookupsInflight) {
    return lookupsInflight;
  }
  lookupsInflight = listLookups({ activeOnly: true })
    .then((data) => {
      cachedLookups = data;
      lookupsInflight = null;
      return data;
    })
    .catch((err) => {
      lookupsInflight = null;
      throw err;
    });
  return lookupsInflight;
}

const doctorsInflight = new Map();

function doctorsCacheKey(roleType) {
  return roleType && String(roleType).trim()
    ? String(roleType).trim().toLowerCase()
    : "__all__";
}

export async function createDiagnosticDoctor(payload) {
  const res = await axios.post(`${BASE}add_diagnostic_doctor/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

export async function getDiagnosticDoctors(roleType) {
  const key = doctorsCacheKey(roleType);
  if (doctorsInflight.has(key)) {
    return doctorsInflight.get(key);
  }

  const qs =
    roleType && String(roleType).trim()
      ? `?role_type=${encodeURIComponent(String(roleType).trim())}`
      : "";

  const request = axios
    .get(`${BASE}get_all_diagnostic_doctors${qs}`, {
      headers: authHeaders(),
    })
    .then((res) => {
      const data = res?.data?.data;
      return Array.isArray(data) ? data : [];
    })
    .finally(() => {
      doctorsInflight.delete(key);
    });

  doctorsInflight.set(key, request);
  return request;
}

export async function updateDiagnosticDoctor(payload) {
  const res = await axios.post(`${BASE}edit_diagnostic_doctor/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

export async function deleteDiagnosticDoctor(diagnosticDoctorId) {
  const res = await axios.post(
    `${BASE}delete_diagnostic_doctor/`,
    { diagnostic_doctor_id: diagnosticDoctorId },
    { headers: authHeaders() }
  );
  return res?.data;
}

export async function getStates() {
  const res = await axios.get(`${BASE}get_all_states/`, {
    headers: authHeaders(),
  });
  return Array.isArray(res?.data?.state_list) ? res.data.state_list : [];
}

export async function getDistricts(stateName) {
  const res = await axios.post(
    `${BASE}get_filtered_districts/`,
    { state_name: stateName },
    { headers: authHeaders() }
  );
  return Array.isArray(res?.data?.district_list) ? res.data.district_list : [];
}

export function roleLabel(value) {
  const match = DIAGNOSTIC_DOCTOR_ROLES.find(
    (r) => r.value === norm(value)
  );
  return match ? match.label : value || "-";
}

export function isDiagnosticDoctorRole(role) {
  const r = norm(role);
  return r === "radiologist" || r === "pathologist";
}

function isNumericId(value) {
  if (value == null || value === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && String(value).trim() === String(n);
}

/** Persist diagnostic-doctor context from login payload for downstream apps. */
export function persistDiagnosticDoctorSession(data = {}) {
  const profile =
    data.diagnostic_doctor ||
    data.diagnostic_doctor_profile ||
    data.DiagnosticDoctorRecord ||
    data.profile ||
    data;

  const rawId = profile.diagnostic_doctor_id ?? data.diagnostic_doctor_id;
  const pk =
    profile.diagnostic_doctor_pk ??
    data.diagnostic_doctor_pk ??
    (isNumericId(rawId) ? rawId : null);
  const code =
    profile.diagnostic_doctor_code ??
    data.diagnostic_doctor_code ??
    (!isNumericId(rawId) && rawId != null && rawId !== "" ? rawId : null);
  const groupId = profile.service_group_id ?? data.service_group_id;
  const classId =
    profile.service_classification_id ?? data.service_classification_id;
  const roleType = profile.role_type ?? data.role_type ?? profile.role;

  if (rawId != null && rawId !== "") {
    sessionStorage.setItem("diagnostic_doctor_id", String(rawId));
  } else {
    sessionStorage.removeItem("diagnostic_doctor_id");
  }

  if (pk != null && pk !== "") {
    sessionStorage.setItem("diagnostic_doctor_pk", String(pk));
  } else {
    sessionStorage.removeItem("diagnostic_doctor_pk");
  }

  if (code != null && code !== "") {
    sessionStorage.setItem("diagnostic_doctor_code", String(code));
  } else {
    sessionStorage.removeItem("diagnostic_doctor_code");
  }

  if (groupId != null && groupId !== "") {
    sessionStorage.setItem("service_group_id", String(groupId));
  } else {
    sessionStorage.removeItem("service_group_id");
  }

  if (classId != null && classId !== "") {
    sessionStorage.setItem("service_classification_id", String(classId));
  } else {
    sessionStorage.removeItem("service_classification_id");
  }

  const role = norm(roleType);
  if (role === "pathologist" || role === "radiologist") {
    sessionStorage.setItem("diagnostic_doctor_role_type", role);
  }

  const name = [profile.salutation, profile.first_name, profile.last_name]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ");
  if (name) {
    sessionStorage.setItem("diagnostic_doctor_name", name);
  }

  const signatureRaw =
    profile.doctor_signature ??
    data.doctor_signature ??
    profile.signature ??
    data.signature;
  let signature = "";
  if (typeof signatureRaw === "string") {
    signature = signatureRaw;
  } else if (Array.isArray(signatureRaw) && signatureRaw[0]) {
    signature =
      signatureRaw[0].base64 ||
      signatureRaw[0].content ||
      signatureRaw[0] ||
      "";
  } else if (signatureRaw && typeof signatureRaw === "object") {
    signature = signatureRaw.base64 || signatureRaw.content || "";
  }
  if (signature) {
    try {
      sessionStorage.setItem("diagnostic_doctor_signature", String(signature));
    } catch (err) {
      // Signature images can exceed sessionStorage quota; reports still print
      // a name line if the image cannot be cached here.
    }
  }
}

export function diagnosticDoctorLoginPath(role) {
  const r = norm(role);
  if (r === "radiologist") return "/radiology-services";
  if (r === "pathologist") return "/laboratories";
  return null;
}
